import { useState, useEffect, useCallback } from 'react';
import userService from '../services/userService';

// Global cache for user data across the entire application
const globalUserCache = new Map();
const pendingRequests = new Map();

export const useUserData = () => {
  const [userDataMap, setUserDataMap] = useState(new Map());

  // Get user data for a single email
  const getUserData = useCallback(async (email) => {
    if (!email) return null;

    // Check if we already have the data
    if (globalUserCache.has(email)) {
      return globalUserCache.get(email);
    }

    // Check if there's already a pending request for this email
    if (pendingRequests.has(email)) {
      return pendingRequests.get(email);
    }

    // Create a new promise for this request
    const userPromise = userService.getUserByEmail(email)
      .then(userData => {
        // Cache the result
        globalUserCache.set(email, userData);
        // Update local state
        setUserDataMap(prev => new Map(prev.set(email, userData)));
        // Remove from pending requests
        pendingRequests.delete(email);
        return userData;
      })
      .catch(error => {
        console.warn(`Error loading user data for ${email}:`, error);
        // Create fallback data
        const fallbackData = {
          email: email,
          name: email.split('@')[0],
          photoUrl: null,
          initials: email.charAt(0).toUpperCase()
        };
        // Cache the fallback
        globalUserCache.set(email, fallbackData);
        // Update local state
        setUserDataMap(prev => new Map(prev.set(email, fallbackData)));
        // Remove from pending requests
        pendingRequests.delete(email);
        return fallbackData;
      });

    // Store the promise in pending requests
    pendingRequests.set(email, userPromise);
    return userPromise;
  }, []);

  // Get user data for multiple emails
  const getUserDataBatch = useCallback(async (emails) => {
    if (!emails || emails.length === 0) return {};

    const results = {};
    const uncachedEmails = [];

    // Check cache first
    for (const email of emails) {
      if (globalUserCache.has(email)) {
        results[email] = globalUserCache.get(email);
      } else {
        uncachedEmails.push(email);
      }
    }

    // Fetch uncached emails in parallel
    if (uncachedEmails.length > 0) {
      const promises = uncachedEmails.map(email => getUserData(email));
      const userDataArray = await Promise.all(promises);
      
      uncachedEmails.forEach((email, index) => {
        results[email] = userDataArray[index];
      });
    }

    return results;
  }, [getUserData]);

  // Preload user data for multiple emails
  const preloadUserData = useCallback(async (emails) => {
    if (!emails || emails.length === 0) return;
    
    const uniqueEmails = [...new Set(emails)].filter(email => !globalUserCache.has(email));
    if (uniqueEmails.length === 0) return;

    await getUserDataBatch(uniqueEmails);
  }, [getUserDataBatch]);

  // Clear cache
  const clearCache = useCallback(() => {
    globalUserCache.clear();
    pendingRequests.clear();
    setUserDataMap(new Map());
  }, []);

  // Get cached user data (synchronous)
  const getCachedUserData = useCallback((email) => {
    return globalUserCache.get(email) || null;
  }, []);

  return {
    getUserData,
    getUserDataBatch,
    preloadUserData,
    clearCache,
    getCachedUserData,
    userDataMap
  };
}; 